import cx from 'classnames';
import React, {
    useContext,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';

import {useOnClickOutside} from '@pexip/hooks';

import {FontVariant} from '../../../../design-tokens/constants';
import {useFocusFrom} from '../../../hooks/useFocusFrom';
import {ThemeContext, ThemeProvider} from '../../../themes/ThemeContext';
import type {
    ExtendedSizeModifier,
    ModalSizeModifier,
} from '../../../types/sizes';
import type {ColorScheme} from '../../../types/variants';
import {sizeToPadding} from '../../../utils/helpers';
import {TestId} from '../../../utils/testIds';
import type {BoxProps} from '../../elements/Box/Box';
import {Box} from '../../elements/Box/Box';
import {Draggable} from '../../elements/Draggable/Draggable.view';
import {Portal} from '../../elements/Portal/Portal';
import {Scrollbars} from '../../elements/Scrollbars/Scrollbars';
import {Text} from '../../elements/Text/Text';
import {Tooltip} from '../../elements/Tooltip/Tooltip';
import {initializeFocus, useFocusTrap} from '../../modules/Focus/useFocusTrap';
import {ModalCloseButton} from '../CloseButton/ModalCloseButton.view';

import styles from './Modal.module.scss';

export enum ModalType {
    Overlay = 'overlay',
    Positioned = 'positioned',
}

const Div: React.FC<React.ComponentProps<'div'>> = ({children, ...props}) => (
    <div {...props}>{children}</div>
);

export interface ModalHandle {
    focus: () => void;
}

export const Modal = React.forwardRef<
    ModalHandle,
    React.ComponentProps<'div'> & {
        canDrag?: boolean;
        closeButton?: typeof ModalCloseButton;
        closeButtonAriaLabel?: string;
        closeOnOutsideClick?: boolean;
        colorScheme?: ColorScheme;
        hasBackground?: boolean;
        isOpen: boolean;
        modalType?: ModalType;
        onClose?: () => void;
        padding?: ExtendedSizeModifier;
        scrollbarsAriaLabel?: string;
        sizeModifier?: ModalSizeModifier;
        testIdCloseButton?: string;
        uniqueTitle?: string;
        withCloseButton?: boolean;
        withMask?: boolean;
        withAlternativeMaskColor?: boolean;
        withScrollbars?: boolean;
        wrapperClassName?: string;
        presentationElementClassName?: string;
        zIndex?: number | string;
    } & Pick<
            BoxProps,
            | 'footerContent'
            | 'footerPadding'
            | 'hasFooterShadow'
            | 'headerContent'
            | 'headerPadding'
        >
>(
    (
        {
            canDrag = false,
            children,
            className,
            closeButton: CloseButton = ModalCloseButton,
            closeButtonAriaLabel = 'Close tooltip',
            closeOnOutsideClick = true,
            colorScheme = 'light',
            footerContent,
            footerPadding,
            hasBackground,
            hasFooterShadow,
            headerContent,
            headerPadding,
            isOpen = false,
            modalType = ModalType.Overlay,
            onClose,
            padding = 'medium',
            scrollbarsAriaLabel,
            sizeModifier = 'small',
            style,
            testIdCloseButton = TestId.ButtonModalClose,
            uniqueTitle,
            withCloseButton = false,
            withMask = true,
            withAlternativeMaskColor = false,
            withScrollbars = true,
            wrapperClassName,
            presentationElementClassName,
            zIndex,
            ...props
        },
        ref,
    ) => {
        const tryToClose = () => {
            if (closeOnOutsideClick) {
                onClose?.();
            }
        };
        const outsideClickRef = useOnClickOutside(tryToClose);
        const {focusTrapRef, setFocusTrapRef} = useFocusTrap(isOpen);
        const floatRef = React.useRef<HTMLDivElement>(null);
        const [positionedModalElement, setPositionedModalElement] =
            useState<HTMLDivElement | null>(null);

        const {focusFrom: focusPositionedModal} = useFocusFrom(
            positionedModalElement,
        );

        const Container = withScrollbars ? Scrollbars : Div;

        useImperativeHandle(ref, () => ({
            focus: () => {
                if (focusTrapRef.current instanceof HTMLElement) {
                    initializeFocus(focusTrapRef.current);
                } else if (positionedModalElement) {
                    focusPositionedModal();
                }
            },
        }));

        useEffect(() => {
            const closeOnEscape = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    onClose?.();
                }
            };
            isOpen
                ? document.addEventListener('keydown', closeOnEscape)
                : document.removeEventListener('keydown', closeOnEscape);
            return () => document.removeEventListener('keydown', closeOnEscape);
        }, [isOpen, onClose]);

        const {colorScheme: defaultColorScheme} = useContext(ThemeContext);

        let modal = (
            <ThemeProvider colorScheme={colorScheme ?? defaultColorScheme}>
                <Box
                    className={cx(
                        styles.modal,
                        styles[modalType],
                        sizeModifier !== 'none' && styles[sizeModifier],
                        !canDrag && className,
                    )}
                    ref={
                        modalType === ModalType.Positioned
                            ? (element: HTMLDivElement | null) => {
                                  setPositionedModalElement(element);
                              }
                            : setFocusTrapRef
                    }
                    hasBackground={hasBackground}
                    headerContent={headerContent}
                    footerContent={footerContent}
                    headerPadding={headerPadding}
                    footerPadding={footerPadding}
                    hasFooterShadow={hasFooterShadow}
                    {...(!canDrag && {style})}
                >
                    <Container
                        className={styles.scrollbars}
                        {...(withScrollbars
                            ? {ariaLabel: scrollbarsAriaLabel}
                            : {'aria-label': scrollbarsAriaLabel})}
                    >
                        <div
                            role="presentation"
                            ref={outsideClickRef}
                            className={cx(
                                styles[sizeToPadding(padding)],
                                presentationElementClassName,
                            )}
                        >
                            <div
                                role="dialog"
                                aria-modal="true"
                                aria-label={uniqueTitle}
                            >
                                {withCloseButton && (
                                    <CloseButton
                                        aria-label={closeButtonAriaLabel}
                                        className={styles.closeButton}
                                        data-testid={testIdCloseButton}
                                        onClose={onClose}
                                    />
                                )}
                                {children}
                            </div>
                        </div>
                    </Container>
                </Box>
            </ThemeProvider>
        );

        if (canDrag) {
            modal = (
                <Draggable
                    className={className}
                    style={style}
                    floatRoot={floatRef}
                >
                    {modal}
                </Draggable>
            );
        }

        if (withMask) {
            modal = (
                <div
                    role="presentation"
                    className={cx(
                        styles.modalMask,
                        {
                            [styles.alternativeModalMaskColor]:
                                withAlternativeMaskColor,
                        },
                        wrapperClassName,
                    )}
                    data-testid={TestId.ModalMask}
                >
                    {modal}
                </div>
            );
        }

        if (!isOpen) {
            return null;
        }

        return (
            <Portal>
                {/* biome-ignore lint/a11y/useSemanticElements: the component props and ref are typed for a div */}
                <div
                    className={styles.modalWrapper}
                    role="region"
                    ref={floatRef}
                    // Keeps the modal interactive when it is portalled over a
                    // react-aria modal overlay, which otherwise marks outside
                    // DOM (including this portal) as `inert`.
                    data-react-aria-top-layer="true"
                    {...props}
                    {...(zIndex === undefined ? {} : {style: {zIndex}})}
                >
                    {modal}
                </div>
            </Portal>
        );
    },
);

Modal.displayName = 'Modal';

export type ModalProps = React.ComponentProps<typeof Modal>;

export const ModalCloseButtonWithTooltip: React.FC<
    React.ComponentProps<typeof ModalCloseButton> &
        React.ComponentProps<typeof HotkeysTooltip>
> = ({
    className,
    colorScheme,
    hotkeyLabel,
    keyCap,
    onClose,
    ...tooltipProps
}) => (
    <HotkeysTooltip
        className={className}
        hotkeyLabel={hotkeyLabel}
        keyCap={keyCap}
        {...tooltipProps}
    >
        <ModalCloseButton colorScheme={colorScheme} onClose={onClose} />
    </HotkeysTooltip>
);

export const HotkeysTooltip: React.FC<
    Omit<
        React.ComponentProps<typeof Tooltip>,
        'text' | 'tooltipContainerClassName'
    > & {
        /** Typically "Close" */
        hotkeyLabel: string;
        keyCap?: string;
    }
> = ({
    children,
    className,
    delay = 0,
    hotkeyLabel,
    keyCap = 'ESC',
    position = 'bottomEnd',
    ...tooltipProps
}) => (
    <Tooltip
        delay={delay}
        position={position}
        text={<HotKey label={hotkeyLabel} keyCap={keyCap} />}
        tooltipContainerClassName={className}
        {...tooltipProps}
    >
        {children}
    </Tooltip>
);
export const HotKey: React.FC<{label: string; keyCap: string}> = ({
    label,
    keyCap,
}) => (
    <div className="d-inline-flex items-center gap-1">
        <Text>{label}</Text>
        <div className={styles.keycap}>
            <Text fontVariant={FontVariant.Small}>{keyCap}</Text>
        </div>
    </div>
);
